import { MetricSeries } from "./metricsApi";

/**
 * Turns a metric group's series into SVG polyline geometry. Kept apart from the component so the
 * maths, which is where an off-by-one silently produces a plausible-looking wrong chart, can be
 * tested without rendering.
 *
 * Charts are drawn inline rather than with a charting library: newui ships none, and a line plot
 * of a few hundred points does not justify a new frontend dependency.
 */

export interface ChartBounds {
  minTime: number;
  maxTime: number;
  minValue: number;
  maxValue: number;
}

export interface PlottedSeries {
  id: string;
  resource: string;
  color: string;
  points: string;
  /** The same line closed down to the baseline, for the translucent fill under it. */
  area: string;
  lastValue: number | null;
}

/** Distinguishable at a glance and stable per series index, so colours do not shift on refresh. */
export const SERIES_COLORS = [
  "#1F6FEB",
  "#D97706",
  "#059669",
  "#DC2626",
  "#7C3AED",
  "#0891B2",
  "#B45309",
  "#4338CA",
  "#BE185D",
  "#15803D",
];

export function colorFor(index: number): string {
  return SERIES_COLORS[index % SERIES_COLORS.length];
}

/** Rounds an axis top up to a readable step: 10s below 100, then 25s, then 100s. */
export function niceCeiling(max: number): number {
  if (max <= 0) return 10;
  const step = max <= 100 ? 10 : max <= 500 ? 25 : 100;
  return Math.ceil(max / step) * step;
}

export function computeBounds(series: MetricSeries[]): ChartBounds | null {
  const times: number[] = [];
  const values: number[] = [];
  for (const s of series) {
    for (const p of s.points) {
      const t = Date.parse(p.time);
      if (!Number.isNaN(t) && Number.isFinite(p.value)) {
        times.push(t);
        values.push(p.value);
      }
    }
  }
  if (times.length === 0) {
    return null;
  }
  return {
    minTime: Math.min(...times),
    maxTime: Math.max(...times),
    // These are percentages, so the axis starts at zero and the top rounds up to a readable
    // number. Anchoring at the data minimum instead exaggerates small movements, which is why
    // V2's chart shows a calm band rather than a jagged one.
    minValue: 0,
    maxValue: niceCeiling(Math.max(...values)),
  };
}

export function plotSeries(
  series: MetricSeries[],
  bounds: ChartBounds,
  width: number,
  height: number,
  padding: { left: number; right: number; top: number; bottom: number }
): PlottedSeries[] {
  const plotWidth = width - padding.left - padding.right;
  const plotHeight = height - padding.top - padding.bottom;
  const timeSpan = bounds.maxTime - bounds.minTime || 1;
  const valueSpan = bounds.maxValue - bounds.minValue || 1;

  return series.map((s, index) => {
    const usable = s.points
      .map((p) => ({ t: Date.parse(p.time), v: p.value }))
      .filter((p) => !Number.isNaN(p.t) && Number.isFinite(p.v))
      .sort((a, b) => a.t - b.t);
    const points = usable
      .map((p) => {
        const x =
          padding.left + ((p.t - bounds.minTime) / timeSpan) * plotWidth;
        const y =
          padding.top +
          plotHeight -
          ((p.v - bounds.minValue) / valueSpan) * plotHeight;
        return `${x.toFixed(1)},${y.toFixed(1)}`;
      })
      .join(" ");
    const baseline = (padding.top + plotHeight).toFixed(1);
    const coords = points.split(" ").filter(Boolean);
    const area =
      coords.length > 1
        ? `${coords[0].split(",")[0]},${baseline} ${points} ${
            coords[coords.length - 1].split(",")[0]
          },${baseline}`
        : "";
    return {
      id: s.id,
      resource: s.resource,
      color: colorFor(index),
      points,
      area,
      lastValue: usable.length ? usable[usable.length - 1].v : null,
    };
  });
}

/** Gridlines from the axis top down to zero, matching V2's 0/10/20/30/40/50 style. */
export function yAxisTicks(bounds: ChartBounds, count = 5): number[] {
  const step = (bounds.maxValue - bounds.minValue) / count;
  return Array.from(
    { length: count + 1 },
    (_, i) => bounds.maxValue - i * step
  );
}

/** Short axis labels: time of day for intraday windows, date once the window spans days. */
export function formatTimeLabel(epochMs: number, spanMs: number): string {
  const d = new Date(epochMs);
  const pad = (n: number) => String(n).padStart(2, "0");
  if (spanMs > 3 * 24 * 3600 * 1000) {
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}`;
  }
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
